import { useContext, useEffect, useMemo, useRef, useState, useCallback, type JSX } from 'react';
import { scrubContent } from 'utils/stringUtils';
import type { Article, ArticlePreview } from 'interfaces/content/articles/Articles';
import PresentedBy, { type PresentedByProps } from 'components/PresentedBy';
import AdminBar from 'components/AdminBar';
import getArticlePageTestId from 'constants/testsIds/articlePage';
import { UserPaywallContext } from 'providers/PaywallProvider';
import { Microsites, RedVentureWidgets, subdomainColors } from 'constants/constants';
import { UserContext } from 'providers/UserProvider';
import type { EventData } from 'services/Gtm/entities';
import Hero from 'components/ArticleV2/HeroV2';
import { PageType } from 'interfaces/content/articles/Post';
import dynamic from 'next/dynamic';
import ErrorBoundary from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import getAdPlacementConfig, { type AdPlacementConfigProps } from 'services/Ad/config/adPlacementConfig';

import Survey from 'components/ArticleV2/Survey';
import { FeatureHeader } from 'components/FeatureHeader';
import { FeatureByline } from 'components/FeatureByline';
import { useIntersectionTracking, type VariantProps } from 'providers/OptimizelyProvider';
import RawHtml from 'utils/miscUtils/rawHtml';
import * as S from './ArticleStyles';
import getCommonTestId from 'constants/testsIds/common';
import useBreakpoint from 'hooks/useBreakpoint';
import { OPTIMIZELY_EVENT_VALUES } from 'providers/OptimizelyProvider/utils';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

const GoogleSourceBanner = dynamic(() => import('components/GoogleSourceBanner'));

const Breadcrumbs = dynamic(() => import('components/Breadcrumbs'));

const TrinityPlayer = dynamic(() => import('components/TrinityPlayer'), {
  ssr: false,
});

const LatestPopularContent = dynamic(() => import('components/ArticleV2/Latest'), {
  ssr: false,
});

const InStream = dynamic(() => import('components/Ad/AdSlot/InStream'), {
  ssr: false,
});

const AboutTheContributors = dynamic(() => import('components/AboutTheContributors'), {
  ssr: false,
});

const ContentWithRVWidgets = dynamic(() => import('components/ContentWithRVWidgets'), {
  ssr: true,
});

const ContentWithEmbed = dynamic(() => import('components/ContentWithEmbed'), {
  ssr: true,
});

const RightRailFlex = dynamic(() => import('components/Ad/AdSlot/RightRailFlex'), {
  ssr: false,
});

const RightRailArticle = dynamic(() => import('components/Ad/AdSlot/RightRailArticle'), {
  ssr: false,
});

export interface ArticleProps {
  article: Article;
  presentedBy?: PresentedByProps;
  footer?: JSX.Element[];
  adFrequency?: number;
  embeds: { node: string; index: number; component: JSX.Element }[];
  headerAds?: JSX.Element[];
  lastPWithoutAds?: number;
  subdomain: string;
  microsite?: string;
  stickyTop?: number | null;
  zipCode?: string;
  latestPosts: {
    posts: ArticlePreview[];
  };
  mostPopular: {
    posts: ArticlePreview[];
  };
  noRecirculationModules?: boolean;
  noTrinityAds: boolean;
}

const ArticlePage: React.FC<ArticleProps> = ({
  article,
  presentedBy,
  footer,
  adFrequency,
  embeds,
  headerAds,
  lastPWithoutAds,
  subdomain,
  microsite,
  stickyTop = null,
  zipCode,
  latestPosts,
  mostPopular,
  noRecirculationModules,
  noTrinityAds,
}) => {
  const { breadcrumbs, content, databaseId, tagNames, postSettings } = article;

  const hideLatestPopularModule = noRecirculationModules || false;
  const [adFrequencyViewPort, setAdFrequencyViewPort] = useState(adFrequency || 0);

  const { userFlags: userVariants } = useOptimizely();
  const [visibleChunks, setVisibleChunks] = useState([0]);
  const chunks = [];
  const chunkSize = 5;
  const getViewportWidth = useCallback(() => window.innerWidth, []);
  useEffect(() => {
    const initialViewportWidth = getViewportWidth();
    if (!adFrequency) {
      if (initialViewportWidth < 768) setAdFrequencyViewPort(adPlacementInfo.mobileAdFrequency);
      else if (initialViewportWidth >= 768) {
        setAdFrequencyViewPort(adPlacementInfo.desktopAdFrequency);
      }
    }
  }, [adFrequency]);
  useIntersectionTracking(
    'author-bio',
    { URL: article.url, value: OPTIMIZELY_EVENT_VALUES.finishedArticle },
    'finished_article',
  );
  useIntersectionTracking(
    'related-video',
    { URL: article.url, value: OPTIMIZELY_EVENT_VALUES.stnPlayer },
    'saw_stn_video',
  );
  useIntersectionTracking(
    'outbrain-container',
    { URL: article.url, value: OPTIMIZELY_EVENT_VALUES.defaultAdImpression },
    'saw_outbrain',
  );
  useIntersectionTracking('latest-popular-module', { URL: article.url, value: 1 }, 'saw_related_articles');
  useIntersectionTracking('article-related', { URL: article.url, value: 1 }, 'saw_related_articles');

  const _optimizelyVariant: AdPlacementConfigProps | null =
    (userVariants?.adsnewlayout as { variables?: { placement?: AdPlacementConfigProps } })?.variables?.placement ||
    null;
  const adPlacementInfo: AdPlacementConfigProps = getAdPlacementConfig(null);

  const rightRailVariants = (userVariants?.desktop_right_rail as { variant: string }) || { variant: 'off' };
  const renderRightRails = rightRailVariants?.variant !== 'off' && rightRailVariants?.variant !== null;
  const renderStaticRightRail = rightRailVariants?.variant === 'r2' || rightRailVariants?.variant === 'r4';
  const renderStickyRightRail = rightRailVariants?.variant === 'r3' || rightRailVariants?.variant === 'r4';

  const shrinkSpacingVariants = userVariants.shrink_spacing_articles as VariantProps | undefined;
  const renderStickySpacing = shrinkSpacingVariants?.enabled ?? false;

  const showGoogleBanner =
    (
      userVariants?.add_us_on_google_banner as {
        variant: string;
      }
    )?.variant === 'on';

  const [shrinkSpacingClass, setshrinkSpacingClass] = useState('');

  useEffect(() => {
    setshrinkSpacingClass(renderStickySpacing ? 'shrink-spacing' : '');
  }, [renderStickySpacing]);

  const cleanedContent = useMemo(() => scrubContent(content), [content]);
  const widgetsInArticle = useMemo(
    () => Object.values(RedVentureWidgets).filter((id) => content.indexOf(id) > -1),
    [content],
  );
  const { paywallCheck } = useContext(UserPaywallContext).paywallState;
  const { userState: user } = useContext(UserContext);
  const [contentRendered, setContentRendered] = useState(false);

  const RawHtmlClass =
    paywallCheck === 'no-paywall' ? 'rawHtml-content content-sentinel' : 'rawHtml-content-no-nativo content-sentinel';

  const primarySection = {
    link: article.primarySection.uri,
    name: article.primarySection.name,
  };

  const breadcrumbsData = <Breadcrumbs {...breadcrumbs} />;

  const hasFeaturedMediaTypeVideo =
    article.featuredMediaType === 'fortune_video' || article.featuredMediaType === 'stn_video_media';

  const trackingData: EventData = {
    HasVideoCD: hasFeaturedMediaTypeVideo,
    articleNameCD: article.title,
    authorNameCD: article.authorNames,
    cmsCategoryCD: 'article-v2',
    cmsSubCategoryCD: article.primarySection.name,
    contentCMSID: article.postId,
    contentPublishedDateCD: article.dateGmt,
    contentTagsCD: article.tagNames,
    hitIDCD: '',
    loginStatusCD: user.isLoggedIn ? 'logged-in' : 'guest',
    platformCD: article.platformCD || 'own',
    premiumCategoryCD: article?.premiumCategory.name || '',
    sessionIDCD: '',
    userAgentCD: typeof window !== 'undefined' ? window.navigator?.userAgent : '',
  };

  const widgetsTrackingData: EventData = {
    ...trackingData,
    contentPlacementCD: 'Recommends Widget',
  };

  const latestModuleTrackingData: EventData = {
    ...trackingData,
    contentPlacementCD: 'latest - bottom',
    eventAction: 'latest module click',
    eventCategory: 'navigation',
  };

  let latestTitle = '';
  if (microsite === Microsites.Crypto) {
    latestTitle = 'Latest in Crypto';
  } else if (primarySection.name === 'Magazine') {
    latestTitle = `Latest from the ${primarySection.name}`;
  } else if (primarySection.name === 'Conferences') {
    latestTitle = `Latest from our ${primarySection.name}`;
  } else {
    latestTitle = `Latest in ${primarySection.name}`;
  }

  useEffect(() => {
    if (chunks.length <= 1 || paywallCheck === 'paywalled') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = parseInt((entry.target as HTMLElement).dataset.index!, 10);
          if (entry.isIntersecting && !visibleChunks.includes(index + 1) && index + 1 < chunks.length) {
            setVisibleChunks((prev) => [...prev, index + 1]);
          }
        });
      },
      { rootMargin: '200px' },
    );
    const sentinels = document.querySelectorAll('.content-sentinel');
    sentinels.forEach((sentinel) => observer.observe(sentinel));
  }, [visibleChunks, chunks.length, contentRendered, paywallCheck]);

  const paragraphs = cleanedContent.split(/<\/p>/);
  for (let i = 0; i < paragraphs.length; i += chunkSize) {
    chunks.push(paragraphs.slice(i, i + chunkSize).join(''));
    chunks.push(`<p className='content-sentinel' data-index=${chunks.length} \>`);
  }

  const trinityRef = useRef<HTMLDivElement>(null);
  const isSurveyEnabled = postSettings?.showSurvey;
  const isAudioPlayerDisabled = postSettings?.hideAudioPlayer;

  const suppressInStreamAds = tagNames.includes('No InStream Ads');

  // State to hold the marginTop value
  const [stickyRightRailMargin, setStickyRightRailMargin] = useState(0);
  const isDesktop = useBreakpoint('xlMin');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const articleContainerElement = document.querySelector('.article-content-container');

    // Sets the offset of the large sticky right rail ad to place below the first instream ad
    const updateOffset = () => {
      // Only run if all conditions are met
      if (!contentRendered) return;
      if (paywallCheck === 'paywalled') {
        setStickyRightRailMargin(renderStaticRightRail ? 36 : 0);
        return;
      }
      if (!isDesktop || suppressInStreamAds || !renderStickyRightRail) return;

      const thirdParagraph = articleContainerElement?.querySelector('p:nth-of-type(2)');

      const firstInStreamGap = 36; // Added gap between the instream ad and sticky right rail ad
      const firstInStreamAdHeight = 250 + 32; // Approximate height of the first instream ad for desktops

      if (thirdParagraph) {
        const thirdParagraphBottom = thirdParagraph.getBoundingClientRect().bottom;
        const articleContainerTop = articleContainerElement?.getBoundingClientRect().top;
        const offset = thirdParagraphBottom - articleContainerTop + firstInStreamAdHeight + firstInStreamGap;
        setStickyRightRailMargin(renderStaticRightRail ? offset - 250 : offset);
      }
    };

    let observer;
    if (articleContainerElement) {
      observer = new MutationObserver(() => updateOffset());
      observer.observe(articleContainerElement, { childList: true, subtree: true });
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [contentRendered, paywallCheck, suppressInStreamAds, isDesktop, renderStickyRightRail, renderStaticRightRail]);

  return (
    // biome-ignore lint/a11y/useSemanticElements: S.Container is article element
    <S.Container
      id='content'
      role='article'
      isFeature={article.templateType === 'feature'}
      className={`${subdomain} ${shrinkSpacingClass}`}
    >
      {showGoogleBanner && <GoogleSourceBanner />}
      {presentedBy && <PresentedBy {...presentedBy} />}

      <ErrorBoundary fragment={UIFragments.ARTICLE_PAGE}>
        {Microsites.Recommends === microsite ? (
          <S.BreadcrumbsContainer>{breadcrumbsData}</S.BreadcrumbsContainer>
        ) : (
          <></>
        )}
      </ErrorBoundary>

      <AdminBar pageId={databaseId} />
      <ErrorBoundary fragment={UIFragments.ARTICLE_PAGE}>
        {article.templateType === 'feature' ? (
          <FeatureHeader
            bubblePresent={article.featureTemplateDisplayLogo!}
            headerColor={article.featureTemplateBackgroundColor!}
            image={article.featureTemplateHeaderImage!}
            primarySection={{
              link: article.primarySection.uri,
              name: article.primarySection.name,
            }}
            primaryPostTag={{
              link: article.primaryPostTag.uri,
              name: !article.primaryPostTag.disablePage ? article.primaryPostTag.name : '',
            }}
            title={article.title}
            dek={article.excerpt || ''}
            displayDek={article.featureTemplateDisplayDek!}
          />
        ) : (
          <Hero article={article} />
        )}
      </ErrorBoundary>

      {headerAds && headerAds.length > 0 && <ErrorBoundary fragment={UIFragments.AD}>{headerAds}</ErrorBoundary>}

      <S.ArticleContentContainer className='article-content-container'>
        {/* Classname needed to be queried for sticky right rail's margin top*/}
        <S.ArticleImageAndContentContainer className={shrinkSpacingClass}>
          <S.ArticleAndAdContainer className={shrinkSpacingClass}>
            {/* Container div needed to be the parent of the 'Related Articles' block */}
            <S.ArticleBlockAndAdSlot
              className={shrinkSpacingClass}
              id='article-content'
            >
              <S.ArticleBlock className={shrinkSpacingClass}>
                {!isAudioPlayerDisabled && (
                  <ErrorBoundary fragment={UIFragments.ARTICLE_PAGE}>
                    <S.TrinityPlayerContainer
                      ref={trinityRef}
                      className='impression-element'
                      data-content-placement='trinity_audio_player'
                    >
                      <TrinityPlayer
                        url={article.url}
                        noTrinityAds={noTrinityAds}
                        paywallCheck={paywallCheck}
                        trinityRef={trinityRef}
                        microsite={subdomain}
                      />
                    </S.TrinityPlayerContainer>
                  </ErrorBoundary>
                )}

                {article.templateType === 'feature' && (
                  <ErrorBoundary fragment={UIFragments.ARTICLE_PAGE}>
                    <FeatureByline
                      article={article}
                      isFeatureArticle
                    />
                  </ErrorBoundary>
                )}

                {isSurveyEnabled && (
                  <ErrorBoundary fragment={UIFragments.SURVEY}>
                    <Survey />
                  </ErrorBoundary>
                )}
                <S.ArticleContentWrapper
                  $subdomain={subdomain}
                  $stickyTop={stickyTop}
                  $color={subdomainColors[subdomain]}
                  className={shrinkSpacingClass}
                >
                  <ErrorBoundary fragment={UIFragments.ARTICLE_PAGE}>
                    <ContentWithRVWidgets
                      widgetIds={widgetsInArticle}
                      microsite={subdomain}
                      zipCode={zipCode}
                      trackingData={widgetsTrackingData}
                      postId={article.postId}
                    >
                      <ContentWithEmbed
                        embedInstructions={embeds.map((embed) => ({ index: embed.index, node: embed.node }))}
                        adFrequency={adFrequencyViewPort}
                        lastPWithoutAds={lastPWithoutAds}
                        pageType={PageType.ARTICLE_V2}
                        setContentRendered={setContentRendered}
                        section={primarySection.name}
                        articleUrl={article.url}
                        suppressInStreamAds={suppressInStreamAds}
                      >
                        {[
                          ...embeds.map((embed) => embed.component),
                          <div
                            key={`article-content-${adFrequencyViewPort}`}
                            data-cy={getArticlePageTestId('CONTENT')}
                            className={RawHtmlClass}
                          >
                            {RawHtml({ html: chunks.join(''), pageId: databaseId })}
                          </div>,
                        ]}
                      </ContentWithEmbed>
                    </ContentWithRVWidgets>
                    {!article.hideAuthors && article.authors.length > 0 && (
                      <AboutTheContributors
                        className={shrinkSpacingClass}
                        authors={article.authors}
                        isEducation={false}
                      />
                    )}
                  </ErrorBoundary>
                  {adPlacementInfo && contentRendered && adPlacementInfo.showEndArticleAd && (
                    <InStream
                      index={100}
                      delay={0}
                      renderAds
                    />
                  )}
                  <ErrorBoundary fragment={UIFragments.ARTICLE_PAGE}>
                    {primarySection.name !== 'Commentary' && footer && <S.FooterStyles>{footer}</S.FooterStyles>}
                  </ErrorBoundary>
                </S.ArticleContentWrapper>
              </S.ArticleBlock>

              {/* Only render the right rails for desktops (>=1280px) and for articles with in stream ads*/}
              {renderRightRails && isDesktop && !suppressInStreamAds && (
                <S.RightRailArticlesWrapper>
                  {renderStaticRightRail && (
                    <S.RightRailStaticAdContainer>
                      <RightRailFlex
                        id={0}
                        dataCy={getCommonTestId('RIGHT_RAIL_AD')}
                      />
                    </S.RightRailStaticAdContainer>
                  )}
                  {renderStickyRightRail && (
                    <RightRailArticle
                      id={1}
                      marginTop={stickyRightRailMargin}
                      dataCy={getCommonTestId('RIGHT_RAIL_STICKY_AD')}
                    />
                  )}
                </S.RightRailArticlesWrapper>
              )}
            </S.ArticleBlockAndAdSlot>
          </S.ArticleAndAdContainer>
          {!hideLatestPopularModule && contentRendered && (
            <>
              <ErrorBoundary fragment={UIFragments.ARTICLE_PAGE}>
                {latestPosts && (
                  <LatestPopularContent
                    content={latestPosts}
                    trackingData={latestModuleTrackingData}
                    className={shrinkSpacingClass}
                    title={latestTitle}
                  />
                )}
              </ErrorBoundary>
              {adPlacementInfo.showBetweenLatestsAndPopular && (
                <InStream
                  index={101}
                  delay={0}
                  renderAds
                />
              )}
              <ErrorBoundary fragment={UIFragments.ARTICLE_PAGE}>
                {mostPopular && (
                  <LatestPopularContent
                    content={mostPopular}
                    trackingData={latestModuleTrackingData}
                    className={shrinkSpacingClass}
                    title='Most Popular'
                  />
                )}
              </ErrorBoundary>
              {mostPopular.posts.length > 0 && adPlacementInfo.showBetweenLatestsAndPopular && (
                <InStream
                  index={102}
                  delay={0}
                  renderAds
                />
              )}
            </>
          )}
        </S.ArticleImageAndContentContainer>
      </S.ArticleContentContainer>
    </S.Container>
  );
};

export { ArticlePage };
